
import {
  computed,
  defineComponent, nextTick, onBeforeUnmount, onMounted, PropType, ref, watch,
} from 'vue'
import IconEye from '@/vue/icons/IconEye.vue'
import IconPlay from '@/vue/icons/IconPlay.vue'
import Loading from '@/vue/public/Loading.vue'
import VideoSlider from '@/vue/public/VideoSlider.vue'
import IconCompress from '@/vue/icons/IconCompress.vue'
import IconExpandWide from '@/vue/icons/IconExpandWide.vue'
import YouTube from 'vue3-youtube'
import periodic, { CancelFunc } from '@/scripts/periodic'
import { Webinar, WebinarSchedule } from '@/types/webinar.d'
import Timeout from '@/types/timeout.d'
import { request, setOption } from '@/scripts/combinedRequest'
import getCountdown from '@/scripts/countdown'
import { useI18n } from 'vue-i18n'
import VButton from '@/vue/public/VButton.vue'
import IconReload from '@/vue/icons/IconReload.vue'
import sleep from '@/scripts/sleep'

declare global {
  interface Document {
    msFullscreenElement: unknown
    mozFullScreenElement: unknown
    webkitFullscreenElement: unknown
    webkitExitFullscreen: () => Promise<void>
    mozCancelFullScreen: () => Promise<void>
    msExitFullscreen: () => Promise<void>
  }

  interface Element {
    webkitRequestFullscreen: () => Promise<void>
    mozRequestFullScreen: () => Promise<void>
    msRequestFullscreen: () => Promise<void>
  }
}

const playerVars = {
  controls: 0,
  disablekb: 1,
  fs: 0,
  iv_load_policy: 3,
  modestbranding: 1,
  rel: 0,
  showinfo: 0,
  origin: window.location.host,
  playsinline: 1,
  autoplay: 0,
}

export default defineComponent({
  name: 'YouTubeVideo',
  components: {
    IconReload,
    VButton,
    IconExpandWide,
    IconCompress,
    VideoSlider,
    Loading,
    IconPlay,
    IconEye,
    YouTube,
  },
  props: {
    webinar: {
      type: Object as PropType<Webinar>,
      required: true,
    },
    schedule: {
      type: Object as PropType<WebinarSchedule>,
      required: true,
    },
    archived: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n()

    const youtubeIframe = ref<typeof YouTube.methods>()

    const youtubeReady = ref(false)
    const youtubePlay = ref(false)
    const videoDuration = ref(0)
    const videoVolume = ref(100)
    const loadingVideo = ref(true)
    const videoTiming = ref(0)
    const videoEnded = ref(false)

    onBeforeUnmount(periodic(1000, () => {
      if (youtubeIframe.value && youtubePlay.value) {
        videoTiming.value = Math.floor(youtubeIframe.value.getCurrentTime())
        setOption('timing', videoTiming.value)
      }
    }))

    const showVideo = computed(() => !props.schedule.finished
      && !(videoDuration.value && props.schedule.left > videoDuration.value))
    const volume = computed<number>({
      get() {
        return videoVolume.value
      },
      set(v) {
        videoVolume.value = v
        youtubeIframe.value?.setVolume(v)
      },
    })

    const dueDate = ref(Date.now())
    const now = ref(Date.now())

    function updateDueDate() {
      dueDate.value = Date.now() - (props.schedule.left * 1000)
    }

    updateDueDate()
    watch(() => props.schedule, updateDueDate)
    onBeforeUnmount(periodic(100, () => {
      now.value = Date.now()
    }))

    const countdown = computed(() => getCountdown(Math.round((dueDate.value - now.value) / 1000), t('public.daysLetter')))

    const fullscreen = ref(false)

    function toggleFullscreen() {
      if (fullscreen.value) {
        if (document.body.requestFullscreen) {
          return document.body.requestFullscreen()
        }
        if (document.body.webkitRequestFullscreen) {
          return document.body.webkitRequestFullscreen()
        }
        if (document.body.msRequestFullscreen) {
          return document.body.msRequestFullscreen()
        }
        if (document.body.mozRequestFullScreen) {
          return document.body.mozRequestFullScreen()
        }
      } else {
        if (document.exitFullscreen) {
          return document.exitFullscreen()
        }
        if (document.msExitFullscreen) {
          return document.msExitFullscreen()
        }
        if (document.mozCancelFullScreen) {
          return document.mozCancelFullScreen()
        }
        if (document.webkitExitFullscreen) {
          return document.webkitExitFullscreen()
        }
      }
      return Promise.resolve()
    }

    watch(fullscreen, () => {
      emit('change-fullscreen', fullscreen.value)
      // eslint-disable-next-line no-console
      toggleFullscreen().catch(console.error)
    })

    function changeFullscreen() {
      fullscreen.value = (
        !!document.fullscreenElement || !!document.msFullscreenElement
        || !!document.mozFullScreenElement || !!document.webkitFullscreenElement
      )
    }

    onMounted(() => {
      document.addEventListener('fullscreenchange', changeFullscreen)
      document.addEventListener('mozfullscreenchange', changeFullscreen)
      document.addEventListener('MSFullscreenChange', changeFullscreen)
      document.addEventListener('webkitfullscreenchange', changeFullscreen)
    })
    onBeforeUnmount(() => {
      document.removeEventListener('fullscreenchange', changeFullscreen)
      document.removeEventListener('mozfullscreenchange', changeFullscreen)
      document.removeEventListener('MSFullscreenChange', changeFullscreen)
      document.removeEventListener('webkitfullscreenchange', changeFullscreen)
    })

    let loadingTimeout: Timeout

    const watchers = ref(0)
    onBeforeUnmount(periodic(10000, async () => {
      const newValue = await request<number>('watchers')
      const delta = newValue - watchers.value
      if (delta) {
        let period = Math.floor(8000 / Math.abs(delta))
        if (period > 500) {
          period = 500
        }
        if (period < 100) {
          period = 100
        }
        const n = Math.ceil(8000 / period)
        const step = Math.ceil(delta / n)
        nextTick(async () => {
          for (let i = 0; i < n; i++) {
            let value = watchers.value + step
            if ((step > 0 && value > newValue) || (step < 0 && value < newValue)) {
              value = newValue
            }
            watchers.value = value
            // eslint-disable-next-line no-await-in-loop
            await sleep(period)
          }
          watchers.value = newValue
        })
      }
    }))

    let cancelInit: CancelFunc

    function initYoutube() {
      youtubeReady.value = true
      if (!props.schedule.finished && youtubeIframe.value && !props.archived) {
        cancelInit = periodic(100, () => {
          if (youtubeIframe.value && youtubeIframe.value.getPlayerState() !== 1) {
            if (!props.schedule.finished && youtubePlay.value) {
              youtubeIframe.value.playVideo()
            }
          } else {
            cancelInit()
          }
        })
      }
      videoDuration.value = youtubeIframe.value?.getDuration() || 0
      videoVolume.value = youtubeIframe.value?.getVolume() || 0
    }

    onBeforeUnmount(() => {
      if (cancelInit) {
        cancelInit()
      }
    })

    return {
      t,

      youtubeIframe,
      playerVars,
      youtubePlay,
      videoDuration,
      loadingVideo,
      videoTiming,

      showVideo,
      volume,
      countdown,
      fullscreen,
      watchers,
      videoEnded,

      initYoutube,
      stateChange({ data }: { data: number }) {
        clearTimeout(loadingTimeout)
        if (youtubeIframe.value) {
          if (data === 1) {
            loadingTimeout = setTimeout(() => {
              loadingVideo.value = false
            }, 4000)
            youtubePlay.value = true
            videoEnded.value = false
          } else {
            loadingVideo.value = true
            if (data === 2) {
              if (props.archived) {
                youtubePlay.value = false
              } else {
                youtubeIframe.value.seekTo(props.schedule.left > 0 ? props.schedule.left : 0, true)
                youtubeIframe.value.playVideo()
              }
            } else if (data === 0 && props.archived) {
              videoEnded.value = true
              youtubePlay.value = false
            }
          }
        }
      },
      setTiming(time: number) {
        if (props.archived) {
          youtubeIframe.value?.seekTo(time, true)
          videoTiming.value = time
        }
      },
      startOnClick() {
        if (youtubeReady.value && youtubeIframe.value) {
          if (youtubePlay.value) {
            if (props.archived) {
              youtubeIframe.value.pauseVideo()
              youtubePlay.value = false
            }
          } else {
            if (!props.archived) {
              youtubeIframe.value.seekTo(props.schedule.left > 0 ? props.schedule.left : 0, true)
            }
            youtubeIframe.value.playVideo()
            youtubePlay.value = true
          }
        }
      },
      repeatVideo() {
        if (youtubeReady.value && youtubeIframe.value) {
          youtubeIframe.value.seekTo(0, true)
        }
      },

      archivedLink: `${document.location.pathname}?archived=1`,
    }
  },
})
